import {EmptyPolicyNumber, PolicyNumber, ValidPolicyNumber} from 'ApplicationEntry/models/EnteredPolicyNumber';
import {EmptyString, isNonEmptyString, NonEmptyString, StringUnion} from 'Core/models/EmptyString';
import * as I from 'immutable';
import {ToPartialMatchOptions} from 'Shared/utilities/MapDiscriminatedUnion';

type IWLProps = {
  plan: StringUnion;
  premium: StringUnion;
  calculatedPremium: StringUnion;
  cwa: StringUnion;
  situsState: StringUnion;
  type: StringUnion;
  faceAmt: StringUnion;
  policyNumber: PolicyNumber;
  premiumType: StringUnion;
  priInsured: StringUnion;
  spouseInsured: StringUnion;
  autoIssueEligibility: boolean;
  replacement: boolean;
  riderCodes: I.List<string>;
  riderAmounts: I.List<number>;
};

export class InvalidNewYorkIWL {
  public readonly key = 'InvalidNewYorkIWL';

  constructor(
    public readonly plan: StringUnion,
    public readonly premium: StringUnion,
    public readonly calculatedPremium: StringUnion,
    public readonly cwa: StringUnion,
    public readonly situsState: StringUnion,
    public readonly type: StringUnion,
    public readonly faceAmt: StringUnion,
    public readonly policyNumber: PolicyNumber,
    public readonly premiumType: StringUnion,
    public readonly priInsured: StringUnion,
    public readonly spouseInsured: StringUnion,
    public readonly autoIssueEligibility: boolean,
    public readonly replacement: boolean,
    public readonly riderCodes: I.List<string>,
    public readonly riderAmounts: I.List<number>
  ) {}
}

export class ValidNewYorkIWL {
  public static createApp({
    plan,
    premium,
    calculatedPremium,
    cwa,
    situsState,
    type,
    faceAmt,
    policyNumber,
    premiumType,
    priInsured,
    spouseInsured,
    autoIssueEligibility,
    replacement,
    riderCodes,
    riderAmounts,
  }: IWLProps) {
    return this.create(
      plan,
      premium,
      calculatedPremium,
      cwa,
      situsState,
      type,
      faceAmt,
      policyNumber,
      premiumType,
      priInsured,
      spouseInsured,
      autoIssueEligibility,
      replacement,
      riderCodes,
      riderAmounts
    );
  }

  public static create(
    plan: StringUnion,
    premium: StringUnion,
    calculatedPremium: StringUnion,
    cwa: StringUnion,
    situsState: StringUnion,
    type: StringUnion,
    faceAmt: StringUnion,
    policyNumber: PolicyNumber,
    premiumType: StringUnion,
    priInsured: StringUnion,
    spouseInsured: StringUnion,
    autoIssueEligibility: boolean,
    replacement: boolean,
    riderCodes: I.List<string>,
    riderAmounts: I.List<number>
  ) {
    return isNonEmptyString(plan)
      ? new ValidNewYorkIWL(
          plan,
          premium,
          calculatedPremium,
          cwa,
          situsState,
          type,
          faceAmt,
          policyNumber,
          premiumType,
          priInsured,
          spouseInsured,
          autoIssueEligibility,
          replacement,
          riderCodes,
          riderAmounts
        )
      : new InvalidNewYorkIWL(
          plan,
          premium,
          calculatedPremium,
          cwa,
          situsState,
          type,
          faceAmt,
          policyNumber,
          premiumType,
          priInsured,
          spouseInsured,
          autoIssueEligibility,
          replacement,
          riderCodes,
          riderAmounts
        );
  }

  public readonly key = 'ValidNewYorkIWL';
  constructor(
    public readonly plan: StringUnion,
    public readonly premium: StringUnion,
    public readonly calculatedPremium: StringUnion,
    public readonly cwa: StringUnion,
    public readonly situsState: StringUnion,
    public readonly type: StringUnion,
    public readonly faceAmt: StringUnion,
    public readonly policyNumber: PolicyNumber,
    public readonly premiumType: StringUnion,
    public readonly priInsured: StringUnion,
    public readonly spouseInsured: StringUnion,
    public readonly autoIssueEligibility: boolean,
    public readonly replacement: boolean,
    public readonly riderCodes: I.List<string>,
    public readonly riderAmounts: I.List<number>
  ) {}
}

export type NewYorkIWL = InvalidNewYorkIWL | ValidNewYorkIWL;

export type NewYorkIWLOptions<TReturn> = ToPartialMatchOptions<NewYorkIWL, 'key', TReturn>;

export const matchNewYorkIWL = <TReturn>(options: NewYorkIWLOptions<TReturn>) => (app: NewYorkIWL): TReturn => {
  switch (app.key) {
    case 'InvalidNewYorkIWL':
      return options.InvalidNewYorkIWL ? options.InvalidNewYorkIWL(app) : options.default(app);
    case 'ValidNewYorkIWL':
      return options.ValidNewYorkIWL ? options.ValidNewYorkIWL(app) : options.default(app);
  }
};

export const EmptyNewYorkIWL = new InvalidNewYorkIWL(
  new EmptyString(),
  new EmptyString(),
  new EmptyString(),
  new EmptyString(),
  new EmptyString(),
  new EmptyString(),
  new EmptyString(),
  EmptyPolicyNumber,
  new EmptyString(),
  new EmptyString(),
  new EmptyString(),
  false,
  false,
  I.List<string>(),
  I.List<number>()
);

export const changePlan = (plan: string) => (app: NewYorkIWL) =>
  ValidNewYorkIWL.createApp({...app, plan: NonEmptyString.create(plan)});
export const changePremium = (premium: string) => (app: NewYorkIWL) =>
  ValidNewYorkIWL.createApp({...app, premium: NonEmptyString.create(premium)});
export const changeCalculatedPremium = (calculatedPremium: string) => (app: NewYorkIWL) =>
  ValidNewYorkIWL.createApp({...app, calculatedPremium: NonEmptyString.create(calculatedPremium)});
export const changeCWA = (cwa: string) => (app: NewYorkIWL) =>
  ValidNewYorkIWL.createApp({...app, cwa: NonEmptyString.create(cwa)});
export const changeSitusState = (situsState: string) => (app: NewYorkIWL) =>
  ValidNewYorkIWL.createApp({...app, situsState: NonEmptyString.create(situsState)});
export const changeType = (type: string) => (app: NewYorkIWL) =>
  ValidNewYorkIWL.createApp({...app, type: NonEmptyString.create(type)});
export const changeFaceAmt = (faceAmt: string) => (app: NewYorkIWL) =>
  ValidNewYorkIWL.createApp({...app, faceAmt: NonEmptyString.create(faceAmt)});
export const changePolicyNumber = (polNum: string) => (app: NewYorkIWL) =>
  ValidNewYorkIWL.createApp({...app, policyNumber: ValidPolicyNumber.create(NonEmptyString.create(polNum))});
export const changePremiumType = (preType: string) => (app: NewYorkIWL) =>
  ValidNewYorkIWL.createApp({...app, premiumType: NonEmptyString.create(preType)});
export const changePriInsured = (priInsured: string) => (app: NewYorkIWL) =>
  ValidNewYorkIWL.createApp({...app, priInsured: NonEmptyString.create(priInsured)});
export const changeSpouseInsured = (spouseInsured: string) => (app: NewYorkIWL) =>
  ValidNewYorkIWL.createApp({...app, spouseInsured: NonEmptyString.create(spouseInsured)});
export const changeAutoIssue = (autoIssueEligibility: boolean) => (app: NewYorkIWL) =>
  ValidNewYorkIWL.createApp({...app, autoIssueEligibility});
export const changeReplacement = (replacement: boolean) => (app: NewYorkIWL) =>
  ValidNewYorkIWL.createApp({...app, replacement});

export const changeRiderCodes = (riderCode: string, index: number) => (app: NewYorkIWL) => {
  const existingList: I.List<string> = app.riderCodes;
  const newList = existingList.set(index, riderCode);
  return ValidNewYorkIWL.createApp({...app, riderCodes: newList});
};

export const changeRiderAmount = (riderAmt: number, index: number) => (app: NewYorkIWL) => {
  const existingList: I.List<number> = app.riderAmounts;
  const newList = existingList.set(index, riderAmt);

  return ValidNewYorkIWL.createApp({...app, riderAmounts: newList});
};
